import { useEffect, useState } from 'react';
import { api, type AdminEntryRow } from '../lib/api.js';

const STATUS_LABEL: Record<string, string> = {
  PROVISIONAL: '仮当選（押印待ち）',
  CONFIRMED: '確定',
  EXPIRED: '期限切れ',
  NO_PRIZE: 'ハズレ',
};

const GRANT_LABEL: Record<string, string> = {
  NOT_APPLICABLE: '付与なし',
  PENDING: '付与待ち',
  GRANTED: '付与済み',
  FAILED: '付与失敗',
};

/**
 * 抽選結果の照会と運用操作。
 *   - 受付番号（お客様の画面に出る番号）で検索
 *   - 特典付与に失敗した結果の再送
 *   - 開発用の手動確定（押印の代わりにスタンプコードを入力。local / development のみ）
 */
export function EntriesPage(): JSX.Element {
  const [q, setQ] = useState('');
  const [rewardGrantStatus, setRewardGrantStatus] = useState('');
  const [items, setItems] = useState<AdminEntryRow[]>([]);
  const [manualConfirmEnabled, setManualConfirmEnabled] = useState(false);
  const [stampCodes, setStampCodes] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = async (): Promise<void> => {
    setError(null);
    try {
      const res = await api.listEntries({ q: q || undefined, rewardGrantStatus: rewardGrantStatus || undefined });
      setItems(res.items);
      setManualConfirmEnabled(res.manualConfirmEnabled);
    } catch (e) {
      setError(e instanceof Error ? e.message : '抽選結果の取得に失敗しました');
    }
  };

  useEffect(() => {
    void load();
    // 初回のみ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (action: () => Promise<unknown>, done: string): Promise<void> => {
    setError(null);
    setMessage(null);
    try {
      await action();
      setMessage(done);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '操作に失敗しました');
    }
  };

  return (
    <>
      <h1>抽選結果</h1>
      <p className="page-desc">
        特典は押印で確定した時点で連携先のシステムに付与されます。付与に失敗した結果や、付与待ちが続く結果はここから再送できます。
      </p>

      <div className="card">
        <div className="row">
          <div className="field">
            <label htmlFor="q">受付番号（先頭の数文字で可）</label>
            <input id="q" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="rewardGrantStatus">特典の付与状態</label>
            <select id="rewardGrantStatus" value={rewardGrantStatus} onChange={(e) => setRewardGrantStatus(e.target.value)}>
              <option value="">すべて</option>
              <option value="FAILED">付与失敗</option>
              <option value="PENDING">付与待ち</option>
            </select>
          </div>
          <div className="field" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button type="button" className="btn" onClick={() => void load()}>
              検索
            </button>
          </div>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {message && <p className="ok">{message}</p>}

      <table>
        <thead>
          <tr>
            <th>受付番号</th>
            <th>抽選日時</th>
            <th>イベント</th>
            <th>店舗</th>
            <th>賞</th>
            <th>状態</th>
            <th>付与</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id}>
              <td>
                <code>{e.id.slice(0, 8)}</code>
              </td>
              <td>{new Date(e.drawnAt).toLocaleString('ja-JP')}</td>
              <td>{e.eventName}</td>
              <td>{e.locationName || e.pcode}</td>
              <td>{e.prize?.name ?? '（ハズレ）'}</td>
              <td>{STATUS_LABEL[e.status] ?? e.status}</td>
              <td title={e.rewardGrantStatus === 'FAILED' ? (e.rewardGrantRef ?? '') : ''}>
                {GRANT_LABEL[e.rewardGrantStatus] ?? e.rewardGrantStatus}
              </td>
              <td>
                {e.status === 'CONFIRMED' && (e.rewardGrantStatus === 'FAILED' || e.rewardGrantStatus === 'PENDING') && (
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => void run(() => api.retryGrant(e.id), '特典付与を再送しました')}
                  >
                    付与を再送
                  </button>
                )}
                {manualConfirmEnabled && e.status === 'PROVISIONAL' && (
                  <span style={{ display: 'flex', gap: 6 }}>
                    <input
                      placeholder="スタンプコード"
                      value={stampCodes[e.id] ?? ''}
                      onChange={(ev) => setStampCodes({ ...stampCodes, [e.id]: ev.target.value })}
                    />
                    <button
                      type="button"
                      className="btn btn--ghost"
                      onClick={() =>
                        void run(
                          () => api.confirmEntry(e.id, stampCodes[e.id]?.trim() || null),
                          '確定しました（開発用の手動確定）',
                        )
                      }
                    >
                      手動確定
                    </button>
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
